import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { EmptyState, Spinner, relativeTime } from '@riskpay/ui-components';
import type { AuditLogEntry } from '@riskpay/types';
import { api } from '../lib/client';

/* ==========================================================================
 *  System audit log
 *
 *  Read-only by construction: `audit_logs` rejects UPDATE and DELETE at the
 *  database level via triggers, so this view cannot offer an edit affordance
 *  even in principle. Rows are paginated by descending id rather than OFFSET,
 *  which stays correct as new entries land during a review.
 * ========================================================================== */

const SEVERITY_TONE: Record<string, string> = {
  INFO: 'badge-neutral',
  NOTICE: 'badge-info',
  WARNING: 'badge-warning',
  CRITICAL: 'badge-danger',
};

function StateDiff({ before, after }: { before: unknown; after: unknown }) {
  if (before === null && after === null) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {[
        ['Before', before],
        ['After', after],
      ].map(([label, value]) => (
        <div key={label as string} className="panel-inset overflow-hidden">
          <p className="border-b border-line-subtle px-3 py-1.5 text-2xs uppercase tracking-wider text-content-faint">
            {label as string}
          </p>
          <pre className="scrollbar-thin max-h-48 overflow-auto px-3 py-2 text-2xs leading-relaxed text-content-muted">
            {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}

export function AuditLog() {
  const [severity, setSeverity] = useState('');
  const [action, setAction] = useState('');
  const [cursors, setCursors] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);

  const beforeId = cursors[cursors.length - 1];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['audit-logs', severity, action, beforeId],
    queryFn: () =>
      api.get<{ data: AuditLogEntry[]; next_before_id: number | null }>('/audit-logs', {
        severity: severity || undefined,
        action: action || undefined,
        before_id: beforeId,
        limit: 50,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-1.5 text-xs text-content-muted">
          <Lock className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
          Append-only. Entries cannot be edited or removed, by anyone, including this account.
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setCursors([]);
            }}
            placeholder="Filter by action, e.g. kyc.approve"
            className="input numeral h-8 w-56 text-2xs"
            aria-label="Filter by action"
          />
          {['', 'NOTICE', 'WARNING', 'CRITICAL'].map((value) => (
            <button
              key={value || 'ALL'}
              type="button"
              onClick={() => {
                setSeverity(value);
                setCursors([]);
              }}
              aria-pressed={severity === value}
              className={`badge ${severity === value ? 'badge-success' : 'badge-neutral'} cursor-pointer`}
            >
              {value ? value.toLowerCase() : 'all'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-content-muted">
          <Spinner /> Loading audit trail
        </div>
      ) : (data?.data ?? []).length === 0 ? (
        <div className="panel">
          <EmptyState title="No entries match" description="Widen the filters to see more of the trail." />
        </div>
      ) : (
        <ul className="panel divide-y divide-line-subtle overflow-hidden">
          {data!.data.map((entry) => {
            const isOpen = expanded === entry.id;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : entry.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-overlay/60"
                >
                  {isOpen ? (
                    <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-faint" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-faint" aria-hidden="true" />
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="numeral text-xs text-content">{entry.action}</span>
                      <span className={`badge ${SEVERITY_TONE[entry.severity] ?? 'badge-neutral'}`}>
                        {entry.severity.toLowerCase()}
                      </span>
                      <span className="text-2xs text-content-faint">
                        {entry.entity_type}
                        {entry.entity_id && ` · ${entry.entity_id.slice(0, 8)}`}
                      </span>
                    </div>
                    <p className="mt-1 text-2xs text-content-subtle">
                      {entry.actor_email ?? 'system'}
                      {entry.actor_role && ` (${entry.actor_role})`}
                      {entry.ip_address && ` · ${entry.ip_address}`}
                      {entry.reason && ` · ${entry.reason}`}
                    </p>
                  </div>

                  <span className="shrink-0 text-2xs text-content-faint" title={new Date(entry.created_at).toLocaleString('en-IN')}>
                    {relativeTime(entry.created_at)}
                  </span>
                </button>

                {isOpen && (
                  <div className="space-y-3 border-t border-line-subtle bg-surface-inset/60 px-4 py-3">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                      {[
                        ['Entry', `#${entry.id}`],
                        ['Actor type', entry.actor_type],
                        ['Request id', entry.request_id ?? '—'],
                        ['Timestamp', new Date(entry.created_at).toLocaleString('en-IN')],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-2xs uppercase tracking-wider text-content-faint">{label}</dt>
                          <dd className="numeral mt-0.5 truncate text-2xs text-content-muted" title={value}>
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <StateDiff before={entry.before_state} after={entry.after_state} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-between">
        <p className="text-2xs text-content-faint">
          {data?.data.length ?? 0} entries{isFetching && ' · refreshing'}
        </p>
        <div className="flex gap-2">
          {cursors.length > 0 && (
            <button type="button" onClick={() => setCursors((c) => c.slice(0, -1))} className="btn btn-secondary h-8 text-xs">
              Newer
            </button>
          )}
          {data?.next_before_id && (
            <button
              type="button"
              onClick={() => setCursors((c) => [...c, data.next_before_id!])}
              className="btn btn-secondary h-8 text-xs"
            >
              Older
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
